import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';

const EMPTY = { firstName: '', lastName: '', email: '', phone: '', source: '', resumeUrl: '', notes: '' };

export function CandidateCreateModal({ open, onClose, onCreated }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);

  const create = useMutation({
    mutationFn: (body) => hrApi.createCandidate(body),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'candidates'] });
      setForm(EMPTY);
      setError(null);
      if (onCreated) {
        onCreated(created);
      }
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  const handle = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  function submit(e) {
    e.preventDefault();
    setError(null);
    create.mutate({
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim() || undefined,
      source: form.source.trim() || undefined,
      resumeUrl: form.resumeUrl.trim() || undefined,
      notes: form.notes.trim() || undefined,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('rec.candidates.modal.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={create.isPending}>
            {create.isPending ? `${t('common.save')}…` : t('common.create')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('rec.candidates.modal.firstName')}</span>
            <input className="input" value={form.firstName} onChange={handle('firstName')} required />
          </label>
          <label className="field">
            <span className="field-label">{t('rec.candidates.modal.lastName')}</span>
            <input className="input" value={form.lastName} onChange={handle('lastName')} required />
          </label>
        </div>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('col.email')}</span>
            <input className="input" type="email" value={form.email} onChange={handle('email')} required />
          </label>
          <label className="field">
            <span className="field-label">{t('rec.candidates.modal.phone')}</span>
            <input className="input" value={form.phone} onChange={handle('phone')} />
          </label>
        </div>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('rec.candidates.modal.source')}</span>
            <input className="input" value={form.source} onChange={handle('source')} />
          </label>
          <label className="field">
            <span className="field-label">{t('rec.candidates.modal.resumeUrl')}</span>
            <input className="input" type="url" value={form.resumeUrl} onChange={handle('resumeUrl')} />
          </label>
        </div>
        <label className="field">
          <span className="field-label">{t('rec.candidates.modal.notes')}</span>
          <textarea className="textarea" rows={2} value={form.notes} onChange={handle('notes')} />
        </label>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
